import { useEffect, useState } from "react";
// REDUX
import { useGetPermissionCatalogueQuery } from "../../redux/roles/roleAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACES
import type { PermissionCatalogue } from "../../interfaces/Roles.interface";
import type { RoleMenuJson } from "../../pages/setupWizard/utils/roleMenu";

/**
 * Fetches the permission catalogue (GET /api/permission-catalogue/): every
 * permission a role can hold, as LiftCloud defines it in code.
 *
 * Resolves a JWT first and keeps the query skipped until it arrives. The
 * catalogue only changes when the server is upgraded, so a cached copy is
 * served instead of refetching on every mount.
 */
export default function useGetPermissionCatalogue(): {
  permissionCatalogue: PermissionCatalogue | null;
  /** The catalogue as a role menu with every flag on; null until it loads. */
  catalogueMenu: RoleMenuJson | null;
  isLoadingPermissionCatalogue: boolean;
  isErrorPermissionCatalogue: boolean;
} {
  const [token, setToken] = useState<string | null>(null);
  const [tokenFailed, setTokenFailed] = useState(false);

  const { data, isError } = useGetPermissionCatalogueQuery(
    { token: token ?? "" },
    { skip: !token, refetchOnMountOrArgChange: false },
  );

  useEffect(() => {
    let cancelled = false;
    GETJwtToken()
      .then((value: string) => {
        if (!cancelled) setToken(value);
      })
      .catch(() => {
        if (!cancelled) setTokenFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const failed = isError || tokenFailed;
  return {
    permissionCatalogue: data ?? null,
    catalogueMenu: data?.menu ?? null,
    isLoadingPermissionCatalogue: !data && !failed,
    isErrorPermissionCatalogue: failed,
  };
}
